import { UserRole } from "@fuse/recoil/pluginStudio/types"

import { ExamplePlugin } from "../types"

export const pollVotingPlugin: ExamplePlugin = {
  id: "poll-voting",
  name: "Live Polls",
  description:
    "Create interactive polls and let guests vote in real-time. Great for decision making and engagement.",
  category: "Interactive",
  tags: ["intermediate", "polls", "voting", "engagement"],
  difficulty: "intermediate",
  preview: "Create polls with multiple options and see live voting results",
  iconName: "lightning",
  files: {
    [UserRole.GUEST]: `import React, { useState, useEffect } from 'react';

export default function GuestView({ pluginApi, eventId, userId, isLoggedIn }) {
  const [polls, setPolls] = useState([]);
  const [userVotes, setUserVotes] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      try {
        const eventData = await pluginApi.getPluginEventInstanceData();
        const userData = await pluginApi.getEventPluginUserData();
        
        setPolls(eventData?.polls || []);
        setUserVotes(userData?.votes || {});
      } catch (error) {
        console.error('Error loading poll data:', error);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  const vote = async (pollId, optionIndex) => {
    const newUserVotes = { ...userVotes, [pollId]: optionIndex };
    setUserVotes(newUserVotes);

    try {
      await pluginApi.saveEventPluginUserData({ votes: newUserVotes });
      
      // Update vote counts in event data
      const eventData = await pluginApi.getPluginEventInstanceData();
      const currentVotes = eventData?.votes || {};
      const pollVotes = currentVotes[pollId] || {};
      const simulatedUserId = 'user-' + Date.now(); // Simulated user ID
      pollVotes[simulatedUserId] = optionIndex;

      await pluginApi.savePluginEventInstanceData({
        ...eventData,
        votes: {
          ...currentVotes,
          [pollId]: pollVotes
        }
      });
    } catch (error) {
      console.error('Error saving vote:', error);
      setUserVotes(userVotes);
    }
  };

  const getVoteCount = (pollId, optionIndex) => {
    // This would need to be loaded from event data in a real implementation
    // For now, we'll simulate it
    return Math.floor(Math.random() * 10);
  };

  const getTotalVotes = (pollId) => {
    // This would need to be loaded from event data in a real implementation
    // For now, we'll simulate it
    return Math.floor(Math.random() * 20);
  };

  if (loading) {
    return (
      <div className="p-6 bg-white rounded-lg shadow-md max-w-2xl mx-auto">
        <div className="text-center">Loading polls...</div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-white rounded-lg shadow-md max-w-2xl mx-auto">
      <h2 className="text-2xl font-bold mb-4">Live Polls 🗳️</h2>
      
      {polls.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No active polls at the moment. Check back later!
        </div>
      ) : (
        <div className="space-y-6">
          {polls.map((poll) => (
            <div key={poll.id} className="border border-gray-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold mb-3">{poll.question}</h3>
              
              <div className="space-y-2">
                {poll.options.map((option, index) => {
                  const voteCount = getVoteCount(poll.id, index);
                  const totalVotes = getTotalVotes(poll.id);
                  const percentage = totalVotes > 0 ? (voteCount / totalVotes) * 100 : 0;
                  const hasVoted = userVotes[poll.id] !== undefined;
                  const isSelected = userVotes[poll.id] === index;

                  return (
                    <div key={index} className="relative">
                      <button
                        onClick={() => !hasVoted && vote(poll.id, index)}
                        disabled={hasVoted}
                        className={\`w-full text-left p-3 rounded border transition-colors \${
                          hasVoted
                            ? isSelected
                              ? 'bg-blue-100 border-blue-500'
                              : 'bg-gray-50 border-gray-200'
                            : 'hover:bg-gray-50 border-gray-200'
                        }\`}
                      >
                        <div className="flex justify-between items-center">
                          <span>{option}</span>
                          {hasVoted && (
                            <span className="text-sm text-gray-600">
                              {voteCount} votes ({percentage.toFixed(1)}%)
                            </span>
                          )}
                        </div>
                        
                        {hasVoted && (
                          <div className="mt-2 bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                              style={{ width: \`\${percentage}%\` }}
                            />
                          </div>
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>

              {userVotes[poll.id] !== undefined && (
                <div className="mt-3 text-sm text-gray-600">
                  Total votes: {getTotalVotes(poll.id)}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}`,
    [UserRole.HOST_EVENT_VIEW]: `import React, { useState, useEffect } from 'react';

export default function HostView({ pluginApi, eventId, userId, isLoggedIn }) {
  const [polls, setPolls] = useState([]);
  const [newPoll, setNewPoll] = useState({ question: '', options: ['', ''] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      try {
        const data = await pluginApi.getPluginEventInstanceData();
        setPolls(data?.polls || []);
      } catch (error) {
        console.error('Error loading polls:', error);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  const addOption = () => {
    setNewPoll(prev => ({
      ...prev,
      options: [...prev.options, '']
    }));
  };

  const updateOption = (index, value) => {
    setNewPoll(prev => ({
      ...prev,
      options: prev.options.map((opt, i) => i === index ? value : opt)
    }));
  };

  const removeOption = (index) => {
    if (newPoll.options.length > 2) {
      setNewPoll(prev => ({
        ...prev,
        options: prev.options.filter((_, i) => i !== index)
      }));
    }
  };

  const createPoll = async () => {
    if (!newPoll.question.trim() || newPoll.options.some(opt => !opt.trim())) {
      alert('Please fill in all fields');
      return;
    }

    const poll = {
      id: Date.now(),
      question: newPoll.question,
      options: newPoll.options.filter(opt => opt.trim()),
      createdAt: new Date().toISOString()
    };

    const updatedPolls = [...polls, poll];
    setPolls(updatedPolls);
    setNewPoll({ question: '', options: ['', ''] });

    try {
      const currentData = await pluginApi.getPluginEventInstanceData();
      await pluginApi.savePluginEventInstanceData({
        ...currentData,
        polls: updatedPolls
      });
    } catch (error) {
      console.error('Error saving poll:', error);
      // Revert on error
      setPolls(polls);
    }
  };

  const deletePoll = async (pollId) => {
    if (window.confirm('Are you sure you want to delete this poll?')) {
      const updatedPolls = polls.filter(poll => poll.id !== pollId);
      setPolls(updatedPolls);

      try {
        const currentData = await pluginApi.getPluginEventInstanceData();
        // Also remove votes for this poll
        const updatedVotes = { ...currentData?.votes };
        delete updatedVotes[pollId];

        await pluginApi.savePluginEventInstanceData({
          ...currentData,
          polls: updatedPolls,
          votes: updatedVotes
        });
      } catch (error) {
        console.error('Error deleting poll:', error);
        // Revert on error
        setPolls(polls);
      }
    }
  };

  const getVoteCount = (pollId, optionIndex) => {
    // This would need to be loaded from event data in a real implementation
    return Math.floor(Math.random() * 10);
  };

  const getTotalVotes = (pollId) => {
    // This would need to be loaded from event data in a real implementation
    return Math.floor(Math.random() * 20);
  };

  if (loading) {
    return (
      <div className="p-6 bg-white rounded-lg shadow-md max-w-2xl mx-auto">
        <div className="text-center">Loading polls...</div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-white rounded-lg shadow-md max-w-2xl mx-auto">
      <h2 className="text-2xl font-bold mb-4">Live Polls (Host View) 🗳️</h2>
      
      {/* Create new poll */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <h3 className="text-lg font-semibold mb-3">Create New Poll</h3>
        
        <input
          type="text"
          value={newPoll.question}
          onChange={(e) => setNewPoll(prev => ({ ...prev, question: e.target.value }))}
          placeholder="Enter your poll question"
          className="w-full px-3 py-2 mb-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
        />

        <div className="space-y-2 mb-3">
          {newPoll.options.map((option, index) => (
            <div key={index} className="flex gap-2">
              <input
                type="text"
                value={option}
                onChange={(e) => updateOption(index, e.target.value)}
                placeholder={\`Option \${index + 1}\`}
                className="flex-1 px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {newPoll.options.length > 2 && (
                <button
                  onClick={() => removeOption(index)}
                  className="px-2 py-2 text-red-500 hover:text-red-700"
                >
                  ✕
                </button>
              )}
            </div>
          ))}
        </div>

        <div className="flex gap-2">
          <button
            onClick={addOption}
            className="px-3 py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
          >
            Add Option
          </button>
          <button
            onClick={createPoll}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
          >
            Create Poll
          </button>
        </div>
      </div>

      {/* Existing polls */}
      <div className="space-y-4">
        {polls.length === 0 ? (
          <p className="text-gray-500 text-center py-4">No polls created yet.</p>
        ) : (
          polls.map((poll) => (
            <div key={poll.id} className="border border-gray-200 rounded-lg p-4">
              <div className="flex justify-between items-start mb-3">
                <h3 className="text-lg font-semibold">{poll.question}</h3>
                <button
                  onClick={() => deletePoll(poll.id)}
                  className="text-red-500 hover:text-red-700 text-sm"
                >
                  Delete
                </button>
              </div>
              
              <div className="space-y-2">
                {poll.options.map((option, index) => {
                  const voteCount = getVoteCount(poll.id, index);
                  const totalVotes = getTotalVotes(poll.id);
                  const percentage = totalVotes > 0 ? (voteCount / totalVotes) * 100 : 0;

                  return (
                    <div key={index} className="p-2 bg-gray-50 rounded">
                      <div className="flex justify-between items-center mb-1">
                        <span>{option}</span>
                        <span className="text-sm text-gray-600">
                          {voteCount} votes ({percentage.toFixed(1)}%)
                        </span>
                      </div>
                      <div className="bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                          style={{ width: \`\${percentage}%\` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-3 text-sm text-gray-600">
                Total votes: {getTotalVotes(poll.id)}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}`,
    [UserRole.HOST_CONFIG_VIEW]: `import React, { useState, useEffect } from 'react';

export default function HostConfigView({ pluginApi, eventId, userId, isLoggedIn }) {
  const [settings, setSettings] = useState({
    allowMultipleVotes: false,
    showResults: 'after_voting', // 'immediately', 'after_voting', 'never'
    allowAnonymousVoting: true,
    maxPollsPerEvent: 10,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadConfig() {
      try {
        const config = await pluginApi.getPluginConfig();
        setSettings({
          allowMultipleVotes: config?.allowMultipleVotes ?? false,
          showResults: config?.showResults ?? 'after_voting',
          allowAnonymousVoting: config?.allowAnonymousVoting ?? true,
          maxPollsPerEvent: config?.maxPollsPerEvent ?? 10,
          ...config
        });
      } catch (error) {
        console.error('Error loading config:', error);
      } finally {
        setLoading(false);
      }
    }
    loadConfig();
  }, []);

  const saveSettings = async () => {
    try {
      await pluginApi.savePluginConfig(settings);
      alert('Poll settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Error saving settings');
    }
  };

  if (loading) {
    return (
      <div className="p-6 bg-white rounded-lg shadow-md max-w-2xl mx-auto">
        <div className="text-center">Loading configuration...</div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-white rounded-lg shadow-md max-w-2xl mx-auto">
      <h2 className="text-2xl font-bold mb-4">Poll Configuration</h2>
      
      <div className="space-y-4">
        <div>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={settings.allowMultipleVotes}
              onChange={(e) => setSettings({...settings, allowMultipleVotes: e.target.checked})}
              className="mr-2"
            />
            Allow users to change their votes
          </label>
          <p className="text-sm text-gray-600 ml-6">Users can update their vote choice after voting</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Show poll results
          </label>
          <select
            value={settings.showResults}
            onChange={(e) => setSettings({...settings, showResults: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="immediately">Show results immediately</option>
            <option value="after_voting">Show results after user votes</option>
            <option value="never">Never show results to guests</option>
          </select>
        </div>

        <div>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={settings.allowAnonymousVoting}
              onChange={(e) => setSettings({...settings, allowAnonymousVoting: e.target.checked})}
              className="mr-2"
            />
            Allow anonymous voting
          </label>
          <p className="text-sm text-gray-600 ml-6">Guests can vote without being logged in</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Maximum polls per event
          </label>
          <input
            type="number"
            value={settings.maxPollsPerEvent}
            onChange={(e) => setSettings({...settings, maxPollsPerEvent: parseInt(e.target.value)})}
            className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            min="1"
            max="50"
          />
        </div>
      </div>

      <button
        onClick={saveSettings}
        className="mt-6 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
      >
        Save Configuration
      </button>
    </div>
  );
}`,
  },
}
